import React, { useState, useEffect, useContext } from "react";
import {
  Typography,
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  message,
  Avatar,
  Descriptions,
  Tooltip,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import usePageTitle from "../services/usePageTitle";
import api from "../services/axiosConfig";
import { validatePassword } from "../utils/passwordValidator";
import { AuthContext } from "../services/authcontext";
import PageLayout from "../components/PageLayout";
import profileHelp from "../content/help/profile.json";

export default function Profile({ pageTitle = "Profile" }) {
  usePageTitle("Profile - SkeyeGate™");

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState({
    username: "",
    email: "",
    role: "",
    token: "",
  });
  const { user: currentUser } = useContext(AuthContext);

  // Watch fields to dynamically check status
  const newPasswordValue = Form.useWatch("newPassword", form);
  const confirmPasswordValue = Form.useWatch("confirmPassword", form);

  const isNewPasswordValid =
    Boolean(newPasswordValue) &&
    validatePassword(newPasswordValue).length === 0;
  const isConfirmPasswordValid =
    Boolean(confirmPasswordValue) &&
    isNewPasswordValid &&
    newPasswordValue === confirmPasswordValue;

  useEffect(() => {
    try {
      const userData = currentUser;
      if (userData) {
        setUser({
          username: userData.username || "",
          email: userData.email || "",
          role:
            userData.roles && userData.roles.length > 0
              ? userData.roles[0].name.replace("ROLE_", "")
              : "User",
          token: userData.token || userData.accessToken || "",
        });
      }
    } catch (e) {
      console.error("Failed to parse user data", e);
    }
  }, [currentUser]);

  const handleChangePassword = async (values) => {
    const { oldPassword, newPassword } = values;
    setLoading(true);
    try {
      await api.post("/auth/change-password", { oldPassword, newPassword });
      message.success("Your password has been updated successfully.");
      form.resetFields();
    } catch (error) {
      message.error(
        error.response?.data?.message ||
          "Failed to change password. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-page" style={{ width: "100%", padding: "24px" }}>
      <PageLayout
        title={pageTitle}
        description="This is your profile page. Here you can view and edit your personal information, manage your account settings, and customize your preferences."
        helpContent={profileHelp}
      >
        <Row gutter={[24, 24]} style={{ marginTop: "32px", width: "100%" }}>
          {/* User Information Panel */}
          <Col xs={24} lg={12} xl={10}>
            <Card
              title="User Information"
              bordered={false}
              style={{
                height: "100%",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <Avatar
                  size={120}
                  icon={<UserOutlined />}
                  style={{ border: "2px solid #f0f0f0" }}
                />
                <Typography.Title
                  level={4}
                  style={{ marginTop: "16px", marginBottom: "0" }}
                >
                  {user.username}
                </Typography.Title>
                <Typography.Text type="secondary">{user.role}</Typography.Text>
              </div>

              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Username">
                  {user.username}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {user.email || "Not Provided"}
                </Descriptions.Item>
                <Descriptions.Item label="Role">{user.role}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* Change Password Panel */}
          <Col xs={24} lg={12} xl={14}>
            <Card
              title="Change Password"
              bordered={false}
              style={{
                height: "100%",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={handleChangePassword}
                autoComplete="off"
                size="large"
              >
                <Form.Item
                  label="Current Password"
                  name="oldPassword"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your current password",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Enter your current password"
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span>
                      New Password&nbsp;
                      <Tooltip title="At least 10 characters · At least 2 uppercase · At least 2 lowercase · At least 2 numbers · At least 2 special characters (!@#$%...)">
                        <InfoCircleOutlined style={{ color: "#888" }} />
                      </Tooltip>
                    </span>
                  }
                  name="newPassword"
                  validateStatus={isNewPasswordValid ? "success" : undefined}
                  extra={
                    isNewPasswordValid ? (
                      <span style={{ color: "#52c41a" }}>
                        Password format is valid
                      </span>
                    ) : null
                  }
                  hasFeedback
                  rules={[
                    { required: true, message: "Please enter a new password" },
                    // ── uses the same shared util as UserDialogs & userService ──
                    () => ({
                      validator(_, value) {
                        if (!value) return Promise.resolve();
                        const errors = validatePassword(value);
                        return errors.length
                          ? Promise.reject(new Error(errors.join(" · ")))
                          : Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Min 10 chars, 2 upper, 2 lower, 2 numbers, 2 symbols"
                  />
                </Form.Item>

                <Form.Item
                  label="Confirm New Password"
                  name="confirmPassword"
                  dependencies={["newPassword"]}
                  validateStatus={
                    isConfirmPasswordValid ? "success" : undefined
                  }
                  extra={
                    isConfirmPasswordValid ? (
                      <span style={{ color: "#52c41a" }}>Passwords match</span>
                    ) : null
                  }
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your new password",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("newPassword") === value)
                          return Promise.resolve();
                        return Promise.reject(
                          new Error("Passwords do not match"),
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Confirm your new password"
                  />
                </Form.Item>

                <Form.Item style={{ marginTop: "32px", marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<LockOutlined />}
                    style={{
                      backgroundColor: "#D97A07",
                      borderColor: "#D97A07",
                    }}
                  >
                    Change Password
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>
        </Row>
      </PageLayout>
    </div>
  );
}
